import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Truck, ShieldCheck, Star, Leaf, MessageCircle, Heart } from "lucide-react";

// Seiten-spezifische Farbpaletten (gemäß Vorgabe):
// Start: blau-grün-orange · Cosplay: blau-grün · 3D-Druck: blau-orange · Produkte: grün-orange
const palettes = {
  "/": {
    bar: "from-[#eef8fd] via-[#f4fbee] to-[#fff7ec]",
    footer: "from-[#bfe3f4] via-[#d9f2c9] to-[#ffe3c2]",
    script: "text-[#1b7fc1]",
    heart: "#6fbf3f",
    icons: ["#1b9ad6", "#3f8f2a", "#1b9ad6", "#6fbf3f"],
  },
  "/cosplay": {
    bar: "from-[#eef8fd] via-[#eef8fd] to-[#f2fbec]",
    footer: "from-[#bfe3f4] via-[#d5eefb] to-[#d9f2c9]",
    script: "text-[#1b7fc1]",
    heart: "#6fbf3f",
    icons: ["#1b9ad6", "#1b9ad6", "#3f8f2a", "#6fbf3f"],
  },
  "/3d-druck": {
    bar: "from-[#eef8fd] via-[#f2f8fd] to-[#fff4e3]",
    footer: "from-[#bfe3f4] via-[#d5eefb] to-[#ffe3c2]",
    script: "text-[#f7941e]",
    heart: "#f7941e",
    icons: ["#1b9ad6", "#f7941e", "#1b9ad6", "#f7941e"],
  },
  "/produkte": {
    bar: "from-[#f2fbec] via-[#f6fbee] to-[#fff4e3]",
    footer: "from-[#d9f2c9] via-[#e8f5d5] to-[#ffe3c2]",
    script: "text-[#3f8f2a]",
    heart: "#f7941e",
    icons: ["#3f8f2a", "#f7941e", "#3f8f2a", "#f7941e"],
  },
};

const usePalette = () => {
  const { pathname } = useLocation();
  return palettes[pathname] || palettes["/"];
};

export const features = [
  { icon: Truck, title: "Schneller Versand", sub: "Zuverlässig & sicher" },
  { icon: ShieldCheck, title: "Sichere Zahlung", sub: "Bequem & zuverlässig" },
  { icon: Star, title: "Individuelle Lösungen", sub: "Für besondere Wünsche und Projekte" },
  { icon: Leaf, title: "Nachhaltig", sub: "Ressourcen schonend" },
];

export const FeaturesBar = () => {
  const p = usePalette();
  return (
    <section className={`bg-gradient-to-r ${p.bar}`}>
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`flex items-center gap-4 ${i > 0 ? "lg:border-l lg:border-slate-200 lg:pl-8" : ""}`}
            >
              <f.icon className="h-11 w-11 shrink-0" strokeWidth={1.6} style={{ color: p.icons[i] }} />
              <div>
                <p className="font-extrabold text-slate-800 font-display">{f.title}</p>
                <p className="text-sm text-slate-500">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const p = usePalette();
  return (
    <footer className="relative overflow-hidden">
      <div className={`bg-gradient-to-r ${p.footer}`}>
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 pt-10 pb-6">
          <div className="flex justify-center mb-4">
            <img src="/logo-full.png" alt="TimerStore Logo" className="h-24 w-auto rounded-2xl shadow-md bg-white" />
          </div>
          <p className={`text-center font-script text-3xl sm:text-4xl ${p.script} flex items-center justify-center gap-2`}>
            Dein Projekt, meine Leidenschaft!
            <Heart className="h-6 w-6" style={{ color: p.heart }} fill={p.heart} fillOpacity={0.3} />
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm font-semibold text-slate-700">
            <div className="flex items-center gap-6">
              <Link to="/impressum" className="hover:text-[#1b9ad6] transition-colors">Impressum</Link>
              <span className="text-slate-300">|</span>
              <Link to="/datenschutz" className="hover:text-[#1b9ad6] transition-colors">Datenschutz</Link>
              <span className="text-slate-300">|</span>
              <Link to="/agb" className="hover:text-[#1b9ad6] transition-colors">AGB</Link>
              <span className="text-slate-300">|</span>
              <Link to="/widerruf" className="hover:text-[#1b9ad6] transition-colors">Widerrufsrecht</Link>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://wa.me/4915678824063"
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#1ebe5a] transition-colors duration-200 shadow-sm"
              >
                <MessageCircle className="h-5 w-5" fill="white" fillOpacity={0.25} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
